import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Profile.module.css';

import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  useGetUserQuery,
  useUpdateUserMutation,
  useGetFormQuery,
} from '../../../../services/settings';
import { useValidateTeacherMutation } from '../../../../services/api';

import {
  selectUserEmail,
  selectLoginType,
  aliasChanged,
} from '../../../../features/authSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useRedux';

import { SignupFormList, SignupSubmitForm } from '../../../../types/login';
import { RegName } from '../../../../types/settings';

import SelectBox from '../../../../components/SelectBox';
import Complete from '../../../Login/Complete';

import {
  limitMaxLen,
  regExName,
  regExPwd,
  removeNoAlphabet,
} from '../../../../utils/util';

import {
  ACCOUNT_INFO,
  CHANGE,
  COMPLETE,
  DEPARTMENT,
  ENTER,
  ENTER_DEPARTMENT_TYPE,
  ENTER_GRADE_TYPE,
  ENTER_MORE_LETTERS,
  ENTER_NICKNAME,
  ENTER_REGION,
  ENTER_SCHOOL_NAME,
  ENTER_SCHOOL_TYPE,
  FEMALE,
  MALE,
  NAME,
  NEW_PASSWORD,
  NICKNAME,
  NOT_MATCH_PWD,
  PASSWORD,
  PRIVACY,
  PROFANITY_WARNING_MESSAGE,
  PUSH_AGREEMENT,
  PWD_VALIDATION_MSG,
  REENTER,
  REGION,
  SCHOOL_GRADE,
  SCHOOL_NAME,
  SELECT_GENDER,
  SUBJECT,
  TEACHER,
  CONFIRM,
  WITHDRAWAL_GUIDE,
  CHANGE_PASSWORD,
  DELETE_ACCOUNT,
} from '../../../../constants';

import { useValidateProfanityMutation } from '../../../../services/common';

export default function Profile() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const email = useAppSelector(selectUserEmail);
  const loginType = useAppSelector(selectLoginType);

  const { data: userData, isLoading: isLoadingUser } = useGetUserQuery();
  const { data: formData = {}, isLoading: isLoadingForm } = useGetFormQuery();
  const [validate] = useValidateTeacherMutation();
  const [submit] = useUpdateUserMutation();
  const [validateProfanity] = useValidateProfanityMutation();

  const [isEnableSubmit, setIsEnableSubmit] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isValid, setIsValid] = useState({ alias: false });
  const [invalidMsg, setInvalidMsg] = useState({ alias: '' });

  const isTeacher = userData?.userType === 1;

  const profileSchema = yup.object({
    pwd: yup
      .string()
      .matches(regExPwd, {
        message: PWD_VALIDATION_MSG,
        excludeEmptyString: true,
      })
      .notRequired(),
    'confirm-password': yup
      .string()
      .oneOf([yup.ref('pwd')])
      .notRequired(),
    alias: yup.string()[isTeacher ? 'notRequired' : 'required'](ENTER_NICKNAME),
    nm: yup
      .string()
      .matches(regExName, {
        message: ENTER_MORE_LETTERS,
        excludeEmptyString: isTeacher,
      })
      [isTeacher ? 'notRequired' : 'required'](ENTER_MORE_LETTERS),
    gender: yup.string()[isTeacher ? 'notRequired' : 'required'](SELECT_GENDER),
    ...(isTeacher
      ? { teacherTypeCd: yup.string().notRequired() }
      : { regionTypeCd: yup.string().required(ENTER_REGION) }),
    schoolTypeCd: yup
      .string()
      [isTeacher ? 'notRequired' : 'required'](ENTER_SCHOOL_TYPE),
    schoolNm: yup
      .string()
      [isTeacher ? 'notRequired' : 'required'](ENTER_SCHOOL_NAME),
    gradeTypeCd: yup
      .string()
      [isTeacher ? 'notRequired' : 'required'](ENTER_GRADE_TYPE),
    ...(isTeacher
      ? { subjectTypeCd: yup.string().notRequired() }
      : { departmentTypeCd: yup.string().required(ENTER_DEPARTMENT_TYPE) }),
    agreeCdList: yup.array(),
  });
  type FormData = yup.InferType<typeof profileSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    getValues,
    setValue,
    control,
    reset,
    setError,
    clearErrors,
    setFocus,
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(profileSchema),
    defaultValues: {
      pwd: userData?.pwd,
      'confirm-password': userData?.pwd,
      alias: userData?.alias,
      nm: userData?.nm,
      gender: userData?.gender,
      ...(isTeacher
        ? { teacherTypeCd: userData?.teacherTypeCd }
        : { regionTypeCd: userData?.regionTypeCd }),
      schoolTypeCd: userData?.schoolTypeCd,
      schoolNm: userData?.schoolNm,
      gradeTypeCd: userData?.gradeTypeCd,
      ...(isTeacher
        ? { subjectTypeCd: userData?.subjectTypeCd }
        : { departmentTypeCd: userData?.departmentTypeCd }),
      agreeCdList: userData?.agreeCdList,
    },
  });

  const onBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const name: RegName = e.target.name as RegName;
    const value = getValues(name);

    if (!value) {
      setIsValid({ ...isValid, [name]: false });
      return;
    }

    try {
      const res = await validateProfanity(value);
      if ('data' in res) {
        setIsValid({ ...isValid, [name]: res.data });
        if (!res.data) {
          setError(name, {
            type: 'filter',
            message: PROFANITY_WARNING_MESSAGE,
          });
          setFocus(name);
          return;
        } else {
          regExName.test(value) && clearErrors(name);
        }
      }
    } catch (error) {
      console.error('Fail to validate profanity', error);
    }

    if (name === 'alias') {
      if (value === userData?.alias) {
        setIsValid({ ...isValid, [name]: false });
        setInvalidMsg({ ...invalidMsg, [name]: '' });
        return;
      }

      try {
        const res = await validate({
          type: name,
          value,
        }).unwrap();

        setIsValid({ ...isValid, [name]: res.message === 'OK' });
        if (res.message !== 'OK') {
          setInvalidMsg({ ...invalidMsg, [name]: res.message });
          setError(name, { type: ' invalid' });
        } else {
          clearErrors(name);
        }
      } catch (error) {
        console.log('Fail to validate', error);
      }
    }
  };

  const onChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = target;

    switch (name) {
      case 'alias':
        target.value = limitMaxLen(value, 10);
        break;
      case 'nm':
        target.value = limitMaxLen(removeNoAlphabet(value), 10);
        break;
      default:
        break;
    }
  };

  const onSubmit = async (data: FormData) => {
    const copiedData = {
      ...data,
    } as SignupSubmitForm;
    data.pwd === '' && (copiedData.pwd = null);
    delete copiedData['confirm-password'];
    copiedData.alias = copiedData.alias?.trim() || (userData?.alias as string);

    copiedData.agreeCdList = copiedData.agreeCdList?.filter(
      (index) => index && index !== ''
    );
    const isNotModified =
      JSON.stringify(copiedData) === JSON.stringify(userData);

    if (isNotModified) {
      navigate('..');
    } else {
      try {
        await submit(copiedData).unwrap();
        setIsComplete(true);
        dispatch(aliasChanged(copiedData.alias));
      } catch (error) {
        console.log('Failed to submit', error);
      }
    }
  };

  const setInputValue = (name: RegName, value: string) => {
    setValue(name, value);
  };

  useEffect(() => {
    reset({
      pwd: userData?.pwd,
      'confirm-password': userData?.pwd,
      alias: userData?.alias,
      nm: userData?.nm,
      gender: userData?.gender,
      teacherTypeCd: userData?.teacherTypeCd,
      regionTypeCd: userData?.regionTypeCd,
      schoolTypeCd: userData?.schoolTypeCd,
      schoolNm: userData?.schoolNm,
      gradeTypeCd: userData?.gradeTypeCd,
      subjectTypeCd: userData?.subjectTypeCd,
      departmentTypeCd: userData?.departmentTypeCd,
      agreeCdList: userData?.agreeCdList,
    });
  }, [reset, userData]);

  useEffect(() => {
    profileSchema
      .isValid(getValues())
      .then((res) => {
        setIsEnableSubmit(res && !errors.alias && !errors.nm);
      })
      .catch((err) => console.log(`Check required values: ${err}`));
  });

  useEffect(() => {
    history.replaceState({}, '', '/home/settings/account');
  }, []);

  if (isLoadingUser || isLoadingForm) return null;

  const { commonType, agreeList } = formData as SignupFormList;
  const {
    departmentTypeList,
    gradeTypeList,
    regionTypeList,
    schoolTypeList,
    subjectTypeList,
    teacherTypeList,
  } = commonType;

  return (
    <>
      <h2>
        {isComplete ? `${ACCOUNT_INFO} ${CHANGE} ${COMPLETE}!` : ACCOUNT_INFO}
      </h2>

      <div className={cx('st_signup')}>
        {!isComplete && <p className={cx('fs18', 'tac', 'mb40')}>{email}</p>}
        {isComplete ? (
          <Complete name="profile" />
        ) : (
          <form>
            {loginType !== 'naver' && (
              <div className={cx('input_area')}>
                <h3>{CHANGE_PASSWORD}</h3>

                <div
                  className={cx('input_box', {
                    checked: errors['pwd'],
                    'input--done': getValues('pwd') && !errors['pwd'],
                  })}
                >
                  <span className={cx('blind')}>
                    {PASSWORD} {ENTER}
                  </span>
                  <input
                    type="password"
                    title={NEW_PASSWORD}
                    placeholder={NEW_PASSWORD}
                    autoComplete="current-password"
                    {...register('pwd')}
                  />
                  {errors['pwd'] && (
                    <span className={cx('msg', 'invalid')}>
                      {errors.pwd?.message}
                    </span>
                  )}
                </div>
                <div
                  className={cx('input_box', {
                    'input--done':
                      getValues('confirm-password') &&
                      !errors['confirm-password'],
                  })}
                >
                  <span className={cx('blind')}>
                    {PASSWORD} {REENTER}
                  </span>
                  <input
                    type="password"
                    title={`${NEW_PASSWORD} ${CONFIRM}`}
                    placeholder={`${NEW_PASSWORD} ${CONFIRM}`}
                    autoComplete="current-password"
                    {...register('confirm-password')}
                  />
                  {getValues('confirm-password') &&
                    errors['confirm-password'] && (
                      <span className={cx('msg', 'invalid')}>
                        {NOT_MATCH_PWD}
                      </span>
                    )}
                </div>
              </div>
            )}

            <div className={cx('input_area')}>
              <h3>{PRIVACY}</h3>
              <div
                className={cx('input_box', {
                  'input--done': isValid.alias,
                  checked:
                    (getValues('alias') !== userData?.alias &&
                      !isValid.alias &&
                      invalidMsg.alias) ||
                    errors.alias,
                })}
              >
                <span className={cx('blind')}>{`${NICKNAME} ${ENTER}`}</span>
                <input
                  type="text"
                  title={`${NICKNAME} ${ENTER}`}
                  autoComplete="nickname"
                  {...register('alias', { onBlur: onBlur, onChange: onChange })}
                />
                {
                  <span className={cx('msg', 'invalid')}>
                    {(getValues('alias') !== userData?.alias &&
                      !isValid.alias &&
                      invalidMsg.alias) ||
                      errors.alias?.message}
                  </span>
                }
              </div>
              <div
                className={cx('input_box', {
                  checked: errors.nm,
                  'input--done': getValues('nm') && !errors.nm,
                })}
              >
                <span className={cx('blind')}>{`${NAME} ${ENTER}`}</span>
                <input
                  type="text"
                  title={`${NAME} ${ENTER}`}
                  placeholder={NAME}
                  autoComplete="name"
                  {...register('nm', { onBlur: onBlur, onChange: onChange })}
                />
                {errors.nm && (
                  <span className={cx('msg', 'invalid')}>
                    {errors.nm?.message}
                  </span>
                )}
              </div>
              <div className={cx('partition_box', 'mt16')}>
                <span className={cx('blind')}>
                  {isTeacher ? TEACHER : REGION}
                </span>
                <Controller
                  render={() => (
                    <SelectBox
                      list={isTeacher ? teacherTypeList : regionTypeList}
                      name={isTeacher ? 'teacherTypeCd' : 'regionTypeCd'}
                      setValue={setInputValue}
                      curValue={
                        isTeacher
                          ? userData?.teacherTypeCd
                          : userData?.regionTypeCd
                      }
                    />
                  )}
                  name="teacherTypeCd"
                  control={control}
                ></Controller>
                <div className={cx('sort_box')}>
                  <div className={cx('radio_wrap')}>
                    <label>
                      <input
                        type="radio"
                        className={cx('blind')}
                        value="M"
                        {...register('gender')}
                      />
                      <span>{MALE}</span>
                    </label>
                  </div>
                  <div className={cx('radio_wrap')}>
                    <label>
                      <input
                        type="radio"
                        className={cx('blind')}
                        value="F"
                        {...register('gender')}
                      />
                      <span>{FEMALE}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className={cx('partition_box')}>
                <span className={cx('blind')}>{SCHOOL_GRADE}</span>
                <div className={cx('select_wrap')}>
                  <Controller
                    render={() => (
                      <SelectBox
                        list={schoolTypeList}
                        name="schoolTypeCd"
                        setValue={setInputValue}
                        curValue={userData?.schoolTypeCd}
                      />
                    )}
                    name="schoolTypeCd"
                    control={control}
                  ></Controller>
                </div>

                <div
                  className={cx('input_box', {
                    checked: errors.schoolNm,
                    'input--done': getValues('schoolNm') && !errors.schoolNm,
                  })}
                >
                  <span className={cx('blind')}>{`${NAME} ${ENTER}`}</span>
                  <input
                    type="text"
                    title={`${NAME} ${ENTER}`}
                    placeholder={SCHOOL_NAME}
                    autoComplete="name"
                    {...register('schoolNm', {
                      onChange: onChange,
                    })}
                  />
                  {errors.schoolNm && (
                    <span className={cx('msg', 'invalid')}>
                      {errors.schoolNm?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className={cx('partition_box', 'mt16')}>
                <span className={cx('blind')}>
                  {isTeacher ? SUBJECT : DEPARTMENT}
                </span>
                <div className={cx('select_wrap')}>
                  <Controller
                    render={() => (
                      <SelectBox
                        list={gradeTypeList}
                        name="gradeTypeCd"
                        setValue={setInputValue}
                        curValue={userData?.gradeTypeCd}
                      />
                    )}
                    name="gradeTypeCd"
                    control={control}
                  ></Controller>
                </div>
                <div className={cx('select_wrap')}>
                  <Controller
                    render={() => (
                      <SelectBox
                        list={isTeacher ? subjectTypeList : departmentTypeList}
                        name={isTeacher ? 'subjectTypeCd' : 'departmentTypeCd'}
                        setValue={setInputValue}
                        curValue={
                          isTeacher
                            ? userData?.subjectTypeCd
                            : userData?.departmentTypeCd
                        }
                      />
                    )}
                    name="subjectTypeCd"
                    control={control}
                  ></Controller>
                </div>
              </div>
            </div>

            <div className={cx('assort_area')}>
              <h3>{PUSH_AGREEMENT}</h3>
              <ul>
                {agreeList.map(({ seq, title, agreeCd }: any, i: number) => (
                  <li key={seq}>
                    <label>
                      <input
                        type="checkbox"
                        className={cx('blind')}
                        {...(register(`agreeCdList.${i}` as const),
                        {
                          defaultChecked:
                            userData?.agreeCdList.includes(agreeCd),
                          onChange: ({ target }) =>
                            setInputValue(
                              `agreeCdList.${i}`,
                              target.checked ? agreeCd : ''
                            ),
                        })}
                      />
                      <span className={cx('clabel')}>{title}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            <div className={cx('assort_area')}>
              <h3>{DELETE_ACCOUNT}</h3>
              <div className={cx('flex', 'quit_box')}>
                <p className={cx('desc')}>
                  <span
                    dangerouslySetInnerHTML={{ __html: WITHDRAWAL_GUIDE }}
                  ></span>
                </p>
                <Link to="../delete" className={cx('gbtn')}>
                  <span>{DELETE_ACCOUNT}</span>
                </Link>
              </div>
            </div>

            <div className={cx('btn_wrap', 'bottom')}>
              <button
                type="button"
                className={cx('btn', 'b_t', 's48', {
                  'is--disabled': !isEnableSubmit,
                })}
                onClick={handleSubmit(onSubmit)}
              >
                <span>{COMPLETE}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
